import { useState, useCallback, useEffect } from "react";
import { Box, Text, Stack, Group, Divider, Button, Slider, Checkbox, NumberInput, TextInput, Tooltip } from "@mantine/core";
import {
  useViewerStore,
  useSelectedModuleInstanceId,
  useSelectedModuleName,
  useSelectedModulePosition,
  useSelectedModuleRootLink,
  useSelectedModuleCanBeChild,
  useSelectedModulePrefix,
  useAvailableConnections,
  useInstances,
} from "../../store";
import { useWebSocket } from "../../WebSocket";
import { recordConnect } from "../../hooks";
import { buildSliderMarks, formatDisplayValue, getJointUnit, snapDisplayValue } from "./jointUnits";
import { ParameterControl, resolveParametric } from "./ParameterControl";
import { PrintBodyModal } from "../../components/PrintBodyModal";
import { apiClient } from "../../api/client";
import type { PrintBodySpec } from "../../api/types";

export function ModuleInspector() {
  const instanceId = useSelectedModuleInstanceId();
  const moduleName = useSelectedModuleName();
  const position = useSelectedModulePosition();
  const rootLink = useSelectedModuleRootLink();
  const canBeChild = useSelectedModuleCanBeChild();
  const currentPrefix = useSelectedModulePrefix();
  const availableConnections = useAvailableConnections();
  const parentInstanceId = useViewerStore((state) => state.selectedModuleParentId);
  const parentLinkName = useViewerStore((state) => state.selectedModuleParentLink);
  const childLinkName = useViewerStore((state) => state.selectedModuleChildLink);
  const instances = useInstances();
  const { sendPreviewConnection, sendConnectModules, sendDeleteInstance, sendDisconnectInstance, sendEnterEditLinksMode, sendEnterEditModuleAxes, sendSetInstancePrefix, sendSetInstanceParameter } = useWebSocket();
  const modules = useViewerStore((state) => state.modules);
  const parametric = resolveParametric(modules, moduleName);
  const placedModule = modules.find((m) => m.id === moduleName || m.name === moduleName);
  const isPrintedPart = placedModule?.generated_from === "body" || placedModule?.generated_from === "branch";
  const [editSpec, setEditSpec] = useState<PrintBodySpec | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editInstanceId, setEditInstanceId] = useState<string | null>(null);

  const [showDetails, setShowDetails] = useState(false);
  const [yaw, setYaw] = useState<number>(0);
  const [pitch, setPitch] = useState<number>(0);
  const [roll, setRoll] = useState<number>(0);
  const [offset, setOffset] = useState<[number, number, number]>([0, 0, 0]);
  const [prefixInput, setPrefixInput] = useState<string>("");
  const rotationUnit = getJointUnit("revolute");
  const rotationMarks = buildSliderMarks(rotationUnit, -Math.PI, Math.PI);

  // Check if module is connected
  const isConnected = parentInstanceId !== null;
  const parentInstance = isConnected
    ? instances.find((inst) => inst.instance_id === parentInstanceId)
    : null;

  // Child link for connection is always the root_link (base_link)
  // This is the module's input connection point to attach to a parent
  const childConnectionLink = rootLink?.name ?? null;

  // Reset state when instance changes
  useEffect(() => {
    setYaw(0);
    setPitch(0);
    setRoll(0);
    setOffset([0, 0, 0]);
    setShowDetails(false);
    // Set prefix input to current prefix or empty string
    setPrefixInput(currentPrefix ?? "");
  }, [instanceId, currentPrefix]);

  // Handle hover preview
  const handleConnectionHover = useCallback(
    (connectionValue: string | null, childLink: string) => {
      if (!instanceId || !childLink) return;

      if (connectionValue) {
        const [parentInstId, parentLink] = connectionValue.split(":");
        sendPreviewConnection(instanceId, childLink, parentInstId, parentLink, true, 0);
      } else {
        sendPreviewConnection(instanceId, "", "", "", false, 0);
      }
    },
    [instanceId, sendPreviewConnection]
  );

  // Handle click to connect immediately
  const handleConnectionClick = useCallback(
    (connectionValue: string, childLink: string) => {
      if (!instanceId || !childLink) return;

      const [parentInstId, parentLink] = connectionValue.split(":");
      recordConnect(instanceId);
      sendConnectModules(instanceId, childLink, parentInstId, parentLink, 0);
      sendPreviewConnection(instanceId, "", "", "", false, 0);
    },
    [instanceId, sendConnectModules, sendPreviewConnection]
  );

  // Send preview with current transform values
  const sendTransformPreview = useCallback(
    (newYaw: number, newPitch: number, newRoll: number, newOffset: [number, number, number]) => {
      if (instanceId && childLinkName && parentInstanceId && parentLinkName) {
        sendPreviewConnection(
          instanceId, childLinkName, parentInstanceId, parentLinkName,
          true, newYaw, newPitch, newRoll, newOffset
        );
      }
    },
    [instanceId, childLinkName, parentInstanceId, parentLinkName, sendPreviewConnection]
  );

  // Commit transform change
  const commitTransform = useCallback(
    (newYaw: number, newPitch: number, newRoll: number, newOffset: [number, number, number]) => {
      if (instanceId && childLinkName && parentInstanceId && parentLinkName) {
        // Transform adjust — no undo recording here as it's a continuous operation
        sendConnectModules(
          instanceId, childLinkName, parentInstanceId, parentLinkName,
          newYaw, newPitch, newRoll, newOffset
        );
        sendPreviewConnection(instanceId, "", "", "", false, 0, 0, 0, [0, 0, 0]);
      }
    },
    [instanceId, childLinkName, parentInstanceId, parentLinkName, sendConnectModules, sendPreviewConnection]
  );

  // Handle yaw change for connected module
  const handleYawChange = useCallback(
    (newYawDisplay: number) => {
      const snapped = snapDisplayValue(newYawDisplay, rotationUnit, -Math.PI, Math.PI);
      const newYaw = rotationUnit.toNative(snapped);
      setYaw(newYaw);
      sendTransformPreview(newYaw, pitch, roll, offset);
    },
    [pitch, roll, offset, rotationUnit, sendTransformPreview]
  );

  const handleYawChangeEnd = useCallback(
    (newYawDisplay: number) => {
      const snapped = snapDisplayValue(newYawDisplay, rotationUnit, -Math.PI, Math.PI);
      const newYaw = rotationUnit.toNative(snapped);
      commitTransform(newYaw, pitch, roll, offset);
    },
    [pitch, roll, offset, rotationUnit, commitTransform]
  );

  // Handle pitch change
  const handlePitchChange = useCallback(
    (newPitchDisplay: number) => {
      const snapped = snapDisplayValue(newPitchDisplay, rotationUnit, -Math.PI, Math.PI);
      const newPitch = rotationUnit.toNative(snapped);
      setPitch(newPitch);
      sendTransformPreview(yaw, newPitch, roll, offset);
    },
    [yaw, roll, offset, rotationUnit, sendTransformPreview]
  );

  const handlePitchChangeEnd = useCallback(
    (newPitchDisplay: number) => {
      const snapped = snapDisplayValue(newPitchDisplay, rotationUnit, -Math.PI, Math.PI);
      const newPitch = rotationUnit.toNative(snapped);
      commitTransform(yaw, newPitch, roll, offset);
    },
    [yaw, roll, offset, rotationUnit, commitTransform]
  );

  // Handle roll change
  const handleRollChange = useCallback(
    (newRollDisplay: number) => {
      const snapped = snapDisplayValue(newRollDisplay, rotationUnit, -Math.PI, Math.PI);
      const newRoll = rotationUnit.toNative(snapped);
      setRoll(newRoll);
      sendTransformPreview(yaw, pitch, newRoll, offset);
    },
    [yaw, pitch, offset, rotationUnit, sendTransformPreview]
  );

  const handleRollChangeEnd = useCallback(
    (newRollDisplay: number) => {
      const snapped = snapDisplayValue(newRollDisplay, rotationUnit, -Math.PI, Math.PI);
      const newRoll = rotationUnit.toNative(snapped);
      commitTransform(yaw, pitch, newRoll, offset);
    },
    [yaw, pitch, offset, rotationUnit, commitTransform]
  );

  // Handle offset change
  const handleOffsetChange = useCallback(
    (axis: 0 | 1 | 2, value: number) => {
      const newOffset: [number, number, number] = [...offset];
      newOffset[axis] = value;
      setOffset(newOffset);
      sendTransformPreview(yaw, pitch, roll, newOffset);
    },
    [yaw, pitch, roll, offset, sendTransformPreview]
  );

  const handleOffsetChangeEnd = useCallback(
    () => {
      commitTransform(yaw, pitch, roll, offset);
    },
    [yaw, pitch, roll, offset, commitTransform]
  );

  const handleDelete = () => {
    if (instanceId) {
      sendDeleteInstance(instanceId);
    }
  };

  const handleDisconnect = () => {
    if (instanceId) {
      sendDisconnectInstance(instanceId);
    }
  };

  const handleEditLinks = () => {
    if (instanceId) {
      sendEnterEditLinksMode(instanceId);
    }
  };

  const handleEditAxes = () => {
    if (instanceId) {
      sendEnterEditModuleAxes(instanceId);
    }
  };

  // Handle prefix update
  const handlePrefixChange = (value: string) => {
    setPrefixInput(value);
  };

  const handlePrefixBlur = () => {
    if (instanceId && prefixInput !== (currentPrefix ?? "")) {
      sendSetInstancePrefix(instanceId, prefixInput);
    }
  };

  const handlePrefixKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePrefixBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  if (!instanceId) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        Click a module in the scene to inspect
      </Text>
    );
  }

  // Format connection options (parent modules' leaf links that can receive this module)
  const connectionOptions = availableConnections.map((conn) => ({
    value: `${conn.instance_id}:${conn.link_name}`,
    label: `${conn.module_name} / ${conn.link_name}`,
  }));

  return (
    <Stack gap="md">
      {/* Header with module name and buttons */}
      <Group justify="space-between" align="flex-start">
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Module</Text>
          <Text size="sm" fw={600}>{moduleName}</Text>
        </Box>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            onClick={handleEditLinks}
          >
            Edit Links
          </Button>
          {/* The axes a module ships, as opposed to the ⇄ in the axis
              overview, which reverses this instance's joint only. */}
          <Button
            size="xs"
            variant="light"
            onClick={handleEditAxes}
          >
            Edit Axes
          </Button>
          <Button
            size="xs"
            color="red"
            variant="light"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Group>
      </Group>

      {/* Prefix input for joint/link naming */}
      <Box>
        <Text size="xs" c="dimmed" mb={4}>Prefix</Text>
        <Group gap="xs" align="flex-start">
          <TextInput
            size="xs"
            value={prefixInput}
            onChange={(e) => handlePrefixChange(e.currentTarget.value)}
            onBlur={handlePrefixBlur}
            onKeyDown={handlePrefixKeyDown}
            placeholder={instanceId ?? ""}
            description="Used for joint/link names in URDF export"
            style={{ flex: 1 }}
            disabled={currentPrefix === ""}
          />
          <Tooltip label={currentPrefix === "" ? "Prefix is disabled" : "Disable prefix (use original joint/link names)"}>
            <Button
              size="xs"
              variant={currentPrefix === "" ? "filled" : "light"}
              color={currentPrefix === "" ? "blue" : "gray"}
              onClick={() => {
                if (instanceId) {
                  if (currentPrefix === "") {
                    // Re-enable prefix (revert to instance_id)
                    sendSetInstancePrefix(instanceId, null);
                  } else {
                    // Disable prefix
                    sendSetInstancePrefix(instanceId, "");
                  }
                }
              }}
            >
              No Prefix
            </Button>
          </Tooltip>
        </Group>
      </Box>

      {/* A printed body or branch: reopen it in the designer and reprint in place */}
      {isPrintedPart && placedModule && (
        <Box>
          <Text size="xs" c="dimmed" mb={4}>
            Printed part &mdash; {placedModule.name}
          </Text>
          <Button
            size="xs"
            variant="light"
            onClick={async () => {
              setEditError(null);
              try {
                const response = await apiClient.printedPartSpec(placedModule.id);
                if (response.success && response.spec) {
                  setEditSpec(response.spec);
                  setEditInstanceId(instanceId);
                  setEditOpen(true);
                } else {
                  setEditError(response.error ?? "no spec stored for this part");
                }
              } catch (e) {
                setEditError(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Edit printed part
          </Button>
          {editError && (
            <Text size="xs" c="red" mt={4}>
              {editError}
            </Text>
          )}
        </Box>
      )}
      {/* Kept outside the block above: the swap re-creates the instance and
          the selection may clear, but the designer must stay open to show
          its result. */}
      <PrintBodyModal
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        onPrinted={() => undefined}
        initial={editSpec}
        replaceInstanceId={editInstanceId}
      />

      {/* Size, for an instance of a length-parametrised module */}
      {parametric && (
        <Box>
          <Text size="xs" c="dimmed" mb={4}>
            Size &mdash; {parametric.base.name}
          </Text>
          <Stack gap="xs">
            {parametric.specs.map((spec) => (
              <ParameterControl
                key={spec.name}
                spec={spec}
                value={parametric.values[spec.name] ?? spec.default}
                onCommit={(value) => {
                  if (instanceId) sendSetInstanceParameter(instanceId, spec.name, value);
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Position display */}
      {position && (
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Position</Text>
          <Text size="sm" style={{ fontFamily: "monospace" }}>
            [{position[0].toFixed(3)}, {position[1].toFixed(3)}, {position[2].toFixed(3)}]
          </Text>
        </Box>
      )}

      {/* Connected module info & Transform */}
      {isConnected && parentInstance && (
        <>
          <Box>
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Connected To</Text>
                <Text size="sm">
                  {parentInstance.module_name} / {parentLinkName}
                </Text>
                <Text size="xs" c="dimmed">
                  via {childLinkName}
                </Text>
              </Box>
              <Button
                size="xs"
                color="orange"
                variant="light"
                onClick={handleDisconnect}
              >
                Disconnect
              </Button>
            </Group>
          </Box>

          <Divider label="Transform" labelPosition="center" />

          <Group justify="flex-end">
            <Checkbox
              label="Details"
              size="xs"
              checked={showDetails}
              onChange={(e) => setShowDetails(e.currentTarget.checked)}
            />
          </Group>

          {/* Yaw slider */}
          <Box>
            <Text size="xs" c="dimmed" mb={4}>Yaw (deg)</Text>
            <Slider
              value={rotationUnit.toDisplay(yaw)}
              onChange={handleYawChange}
              onChangeEnd={handleYawChangeEnd}
              min={-180}
              max={180}
              step={rotationUnit.step}
              marks={rotationMarks}
              label={(value) => formatDisplayValue(value, rotationUnit)}
              size="sm"
            />
          </Box>

          {showDetails && (
            <>
              {/* Pitch slider */}
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Pitch (deg)</Text>
                <Slider
                  value={rotationUnit.toDisplay(pitch)}
                  onChange={handlePitchChange}
                  onChangeEnd={handlePitchChangeEnd}
                  min={-180}
                  max={180}
                  step={rotationUnit.step}
                  marks={rotationMarks}
                  label={(value) => formatDisplayValue(value, rotationUnit)}
                  size="sm"
                />
              </Box>

              {/* Roll slider */}
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Roll (deg)</Text>
                <Slider
                  value={rotationUnit.toDisplay(roll)}
                  onChange={handleRollChange}
                  onChangeEnd={handleRollChangeEnd}
                  min={-180}
                  max={180}
                  step={rotationUnit.step}
                  marks={rotationMarks}
                  label={(value) => formatDisplayValue(value, rotationUnit)}
                  size="sm"
                />
              </Box>

              <Divider my="xs" label="Offset (m)" labelPosition="center" />

              {/* Offset inputs */}
              <Group grow>
                <NumberInput
                  label="X"
                  value={offset[0]}
                  onChange={(val) => handleOffsetChange(0, typeof val === 'number' ? val : 0)}
                  onBlur={handleOffsetChangeEnd}
                  size="xs"
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Y"
                  value={offset[1]}
                  onChange={(val) => handleOffsetChange(1, typeof val === 'number' ? val : 0)}
                  onBlur={handleOffsetChangeEnd}
                  size="xs"
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Z"
                  value={offset[2]}
                  onChange={(val) => handleOffsetChange(2, typeof val === 'number' ? val : 0)}
                  onBlur={handleOffsetChangeEnd}
                  size="xs"
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
              </Group>
            </>
          )}
        </>
      )}

      {/* Connection section for unconnected modules */}
      {/* Connection section for unconnected modules with a root_link and can_be_child=true */}
      {!isConnected && childConnectionLink && canBeChild && (
        <>
          <Divider label="Connect" labelPosition="center" />

          {/* Show the connection point (root_link / base_link) */}
          <Text size="xs" c="dimmed">
            Using: {childConnectionLink}
          </Text>

          {/* Parent connection candidates (other modules' leaf links) */}
          {connectionOptions.length > 0 && (
            <Box>
              <Text size="xs" c="dimmed" mb={4}>Connect to:</Text>
              <Stack gap={4}>
                {connectionOptions.map((opt) => (
                  <Box
                    key={opt.value}
                    p="xs"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: 4,
                      cursor: "pointer",
                      transition: "background-color 0.1s",
                    }}
                    onMouseEnter={() => handleConnectionHover(opt.value, childConnectionLink)}
                    onMouseLeave={() => handleConnectionHover(null, childConnectionLink)}
                    onClick={() => handleConnectionClick(opt.value, childConnectionLink)}
                  >
                    <Text size="xs">{opt.label}</Text>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {connectionOptions.length === 0 && (
            <Text size="xs" c="dimmed" ta="center">
              No available connection points on other modules
            </Text>
          )}
        </>
      )}

      {/* No root_link or can_be_child=false means no connection point */}
      {!isConnected && (!childConnectionLink || !canBeChild) && (
        <Text size="xs" c="dimmed" ta="center">
          {!canBeChild
            ? "This module cannot connect to a parent (root-only module)"
            : "This module has no connection point"}
        </Text>
      )}
    </Stack>
  );
}

